'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { TrendingUp, Thermometer, Waves, Mountain, Building2, Compass } from 'lucide-react';

const MOODS = [
  {
    id: 'beach',
    label: 'Beach & Coast',
    icon: <Waves size={20} />,
    color: 'from-blue-500 to-cyan-400',
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    border: 'border-blue-100',
    count: '38 stays',
    hotelId: 'prop-2',
  },
  {
    id: 'desert',
    label: 'Desert Adventure',
    icon: <Thermometer size={20} />,
    color: 'from-orange-500 to-amber-400',
    bg: 'bg-orange-50',
    text: 'text-orange-600',
    border: 'border-orange-100',
    count: '29 stays',
    hotelId: 'prop-4',
  },
  {
    id: 'mountain',
    label: 'Mountain Escape',
    icon: <Mountain size={20} />,
    color: 'from-emerald-600 to-teal-400',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-100',
    count: '21 stays',
    hotelId: 'prop-4',
  },
  {
    id: 'city',
    label: 'City Breaks',
    icon: <Building2 size={20} />,
    color: 'from-violet-600 to-purple-400',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-100',
    count: '148 stays',
    hotelId: 'prop-1',
  },
  {
    id: 'trending',
    label: 'Trending Now',
    icon: <TrendingUp size={20} />,
    color: 'from-rose-500 to-pink-400',
    bg: 'bg-rose-50',
    text: 'text-rose-600',
    border: 'border-rose-100',
    count: '54 stays',
    hotelId: 'prop-1',
  },
  {
    id: 'offbeat',
    label: 'Off the Beaten Path',
    icon: <Compass size={20} />,
    color: 'from-amber-600 to-yellow-400',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-100',
    count: '17 stays',
    hotelId: 'prop-3',
  },
];

const RECENT_SEARCHES = [
  { id: 'rs-1', label: 'Algiers · 2 guests · Apr 25–27', icon: '🏙️', hotelId: 'prop-1' },
  { id: 'rs-2', label: 'Oran · 1 guest · May 1–3', icon: '🌊', hotelId: 'prop-2' },
  { id: 'rs-3', label: 'Tamanrasset · 4 guests · May 10–14', icon: '🏜️', hotelId: 'prop-4' },
];

export default function TravelInspiration() {
  const router = useRouter();

  return (
    <section className="py-10 sm:py-14 bg-white">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Recent Searches */}
        {RECENT_SEARCHES?.length > 0 && (
          <div className="mb-8 sm:mb-12">
            <h3 className="font-display text-base sm:text-lg font-700 text-gray-900 mb-3 sm:mb-4" style={{ fontWeight: 700 }}>
              Your recent searches
            </h3>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
              {RECENT_SEARCHES?.map((s) => (
                <button
                  key={s?.id}
                  onClick={() => router?.push(`/hotel-detail?id=${s?.hotelId}`)}
                  aria-label={`Repeat search: ${s?.label}`}
                  className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-gray-50 hover:bg-primary-50 border border-gray-200 hover:border-primary-200 transition-all duration-150 text-sm font-body text-gray-700 hover:text-primary-600 active:scale-95 min-h-[44px] w-full sm:w-auto"
                >
                  <span>{s?.icon}</span>
                  {s?.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Travel Moods */}
        <div className="mb-2">
          <p className="text-xs font-display font-600 text-accent-500 uppercase tracking-widest mb-1" style={{ fontWeight: 600 }}>
            Find your vibe
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-800 text-gray-900 mb-6 sm:mb-8" style={{ fontWeight: 800 }}>
            Travel by mood
          </h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          {MOODS?.map((mood) => (
            <button
              key={mood?.id}
              onClick={() => router?.push(`/hotel-detail?id=${mood?.hotelId}&mood=${encodeURIComponent(mood?.label)}`)}
              aria-label={`Browse ${mood?.label} — ${mood?.count}`}
              className={`group flex flex-col items-center gap-2 sm:gap-2.5 p-3 sm:p-5 rounded-2xl ${mood?.bg} border ${mood?.border} hover:shadow-md transition-all duration-200 cursor-pointer active:scale-95 min-h-[90px] sm:min-h-[100px]`}
            >
              <div className={`${mood?.text} group-hover:scale-110 transition-transform duration-200`}>
                {mood?.icon}
              </div>
              <div className="text-center">
                <p className={`font-display font-700 text-xs sm:text-sm ${mood?.text} leading-tight`} style={{ fontWeight: 700 }}>
                  {mood?.label}
                </p>
                <p className="text-xs text-gray-500 font-body mt-0.5">{mood?.count}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
